// Import a PRD (new document or new version) with visible read/upload/extraction
// progress and recoverable errors (RF-PRD-01, RF-UI-06). The Idempotency-Key is bound to
// the current content: retrying the same content reuses it (AC-68, RF-PRD-06).
import { useEffect, useRef, useState, type FormEvent } from "react";
import type { PrdImportResult } from "@maestros/contracts";
import { useI18n } from "../i18n/index.tsx";
import { addPrdVersion, importPrd } from "../api/endpoints.ts";
import { isApiError } from "../api/client.ts";
import { newId } from "../lib/storage.ts";
import { PRD_ACCEPT, PrdFileError, formatBytes, readPrdFile, type ReadPrd } from "../lib/prdFile.ts";
import { Dialog } from "../components/Dialog.tsx";
import { Button } from "../components/Button.tsx";
import { CheckboxField, TextAreaField, TextField } from "../components/Field.tsx";
import { Banner, localErrorMessage } from "../components/Feedback.tsx";
import { ProgressBar, StatusPill } from "../components/Status.tsx";
import { EXTRACTION_VIEW } from "../lib/status.ts";
import { PaperclipIcon, XIcon } from "../components/icons.tsx";

export type ImportStage = "idle" | "reading" | "uploading" | "extracting" | "done" | "error";

export function ImportProgress({ stage, fraction }: { stage: ImportStage; fraction: number }) {
  const { t } = useI18n();
  if (stage === "idle" || stage === "done" || stage === "error") return null;
  const label =
    stage === "reading"
      ? t("prdImport.reading", { pct: Math.round(fraction * 100) })
      : stage === "uploading"
        ? t("prdImport.uploading", { pct: Math.round(fraction * 100) })
        : t("prdImport.extracting");
  return (
    <div role="status" aria-live="polite">
      <ProgressBar value={stage === "extracting" ? 100 : Math.round(fraction * 100)} max={100} label={label} />
    </div>
  );
}

export function ImportResultSummary({ result }: { result: PrdImportResult }) {
  const { t } = useI18n();
  const view = EXTRACTION_VIEW[result.extraction_state] ?? EXTRACTION_VIEW["partial"]!;
  return (
    <div className="stack" style={{ gap: 8 }} data-testid="prd-import-result">
      <div className="row row--wrap">
        <StatusPill view={view} />
        <span className="muted">{t("prdImport.version", { n: result.version })}</span>
        {result.replayed ? <span className="badge">{t("prdImport.replayed")}</span> : null}
      </div>
      <p>{t("prdImport.counts", { requirements: result.requirement_count, tasks: result.task_count, pending: result.pending_count })}</p>
      {result.duplicate_content_warning ? <Banner tone="warning">{t("prdImport.duplicateWarning")}</Banner> : null}
      {result.extraction_notes.length > 0 ? (
        <ul className="notes">
          {result.extraction_notes.map((n, i) => (
            <li key={i} className="user-content">
              {n}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** When set, the import becomes a new version of this PRD. */
  prd?: { id: string; title: string } | null;
  onImported: (result: PrdImportResult) => void;
}

export function PrdImportDialog({ open, onClose, projectId, prd, onImported }: Props) {
  const { t } = useI18n();
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [file, setFile] = useState<ReadPrd | null>(null);
  const [stage, setStage] = useState<ImportStage>("idle");
  const [fraction, setFraction] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [retryable, setRetryable] = useState(false);
  const [result, setResult] = useState<PrdImportResult | null>(null);
  const [startExecution, setStartExecution] = useState(false);
  const keyRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setStage("idle");
      setError(null);
      setResult(null);
      setTitle(prd?.title ?? "");
    }
  }, [open, prd]);

  const onPickFile = async (f: File | undefined) => {
    if (!f) return;
    setError(null);
    setStage("reading");
    setFraction(0);
    try {
      const read = await readPrdFile(f, setFraction);
      setFile(read);
      if (!title) setTitle(read.title);
      setStage("idle");
    } catch (e) {
      setStage("error");
      setRetryable(false);
      if (e instanceof PrdFileError) {
        setError(
          e.reason === "unsupported"
            ? t("prdImport.unsupported")
            : e.reason === "too_large"
              ? t("prdImport.tooLarge")
              : e.reason === "empty"
                ? t("prdImport.empty")
                : t("prdImport.readFailed"),
        );
      } else setError(t("prdImport.readFailed"));
    } finally {
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const content = file?.content ?? text;
    if (!title.trim() || !content.trim()) {
      setError(t("prdImport.missing"));
      setStage("error");
      return;
    }
    const fingerprint = `${prd?.id ?? "new"}|${title}|${file?.name ?? ""}|${content.length}|${content.slice(0, 256)}`;
    if (!keyRef.current || keyRef.current.fingerprint !== fingerprint) keyRef.current = { fingerprint, key: newId() };
    const body = {
      title: title.trim(),
      source_kind: file?.source_kind ?? ("text" as const),
      content,
      content_encoding: file?.content_encoding ?? ("utf8" as const),
      start_execution: startExecution,
      intentional_new_version: Boolean(prd),
    };
    setError(null);
    setStage("uploading");
    setFraction(0);
    try {
      const onProgress = (f: number) => {
        setFraction(f);
        if (f >= 1) setStage("extracting");
      };
      const r = prd ? await addPrdVersion(prd.id, body, keyRef.current.key, onProgress) : await importPrd(projectId, body, keyRef.current.key, onProgress);
      setResult(r);
      setStage("done");
      keyRef.current = null;
      onImported(r);
    } catch (err) {
      setStage("error");
      setRetryable(isApiError(err) ? err.retryable : true);
      setError(localErrorMessage(err, t));
    }
  };

  const busy = stage === "reading" || stage === "uploading" || stage === "extracting";
  const formId = "prd-import-form";
  return (
    <Dialog
      open={open}
      onClose={onClose}
      busy={busy}
      wide
      title={prd ? t("prdImport.newVersionTitle", { title: prd.title }) : t("prdImport.title")}
      description={t("prdImport.description")}
      footer={
        stage === "done" ? (
          <Button variant="primary" onClick={onClose}>
            {t("common.done")}
          </Button>
        ) : (
          <>
            <Button onClick={onClose} disabled={busy}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" form={formId} variant="primary" loading={busy}>
              {stage === "error" && retryable ? t("common.retry") : t("prdImport.submit")}
            </Button>
          </>
        )
      }
    >
      {stage === "done" && result ? (
        <ImportResultSummary result={result} />
      ) : (
        <form id={formId} className="stack" onSubmit={onSubmit} noValidate>
          {error ? (
            <Banner tone="error" role="alert" title={t("prdImport.failed")}>
              {error}
              {retryable ? <p className="field-hint">{t("prdImport.retryHint")}</p> : null}
            </Banner>
          ) : null}
          <TextField label={t("prdImport.docTitle")} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={300} disabled={busy || Boolean(prd)} />
          <div className="stack" style={{ gap: 6 }}>
            <span className="field-label">{t("prdImport.source")}</span>
            {file ? (
              <div className="file-chip">
                <PaperclipIcon size={16} />
                <span className="truncate">{file.name}</span>
                <span className="subtle">{formatBytes(file.size)}</span>
                <Button size="sm" variant="ghost" icon={<XIcon size={14} />} onClick={() => setFile(null)} disabled={busy}>
                  {t("common.remove")}
                </Button>
              </div>
            ) : (
              <div className="row row--wrap">
                <Button icon={<PaperclipIcon size={16} />} onClick={() => fileInput.current?.click()} disabled={busy}>
                  {t("prdImport.chooseFile")}
                </Button>
                <span className="field-hint">{t("prdImport.formats")}</span>
              </div>
            )}
            <input ref={fileInput} type="file" accept={PRD_ACCEPT} className="sr-only" tabIndex={-1} aria-hidden="true" data-testid="prd-file-input" onChange={(e) => void onPickFile(e.target.files?.[0])} />
          </div>
          {!file ? (
            <TextAreaField label={t("prdImport.pasteText")} value={text} onChange={(e) => setText(e.target.value)} rows={8} disabled={busy} />
          ) : null}
          <CheckboxField label={t("prdImport.startExecution")} hint={t("prdImport.startExecutionHint")} checked={startExecution} onChange={(e) => setStartExecution(e.target.checked)} disabled={busy} />
          <ImportProgress stage={stage} fraction={fraction} />
        </form>
      )}
    </Dialog>
  );
}
